import React, { useState, forwardRef, ForwardedRef } from 'react';
import { 
    IconButton, Menu, MenuItem, Avatar, ListItemIcon, Divider, 
    Box, Typography, Snackbar, Fade, Tooltip, useTheme 
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router';
import { useEasyBrowserContext } from '../browser';
import tokenService from '../../../services/tokenservice';
import { salvarConfig } from '../../../services/configusuarioservice';
import ModalTrocarSenha from '../../usuario/usuariomodalsenha';
import { NomeTema, esquemasCores } from '../taskbar/tema';

const UsuarioMenu = forwardRef((_props: unknown, _ref: ForwardedRef<any>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isTrocarSenhaModalOpen, setIsTrocarSenhaModalOpen] = useState(false);
    const [notificacao, setNotificacao] = useState({ open: false, mensagem: '' });
    
    const { usuario, taskSelecionada, setConfigTema, configTema } = useEasyBrowserContext()!;
    const navigate = useNavigate();
    const theme = useTheme();

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const alterarTema = async (tema: NomeTema) => {
        if (setConfigTema) setConfigTema(tema);
        try {
            await salvarConfig("tema_sistema", "APARENCIA", JSON.stringify({"tema" : tema}));
            setNotificacao({ open: true, mensagem: `Tema ${tema} aplicado!` });
        } catch (e) { console.error(e); }
    };

    const definirHome = async () => {
        if (!taskSelecionada) return;
        await salvarConfig("tela_inicial", "TELA", JSON.stringify({ id: taskSelecionada.id }));
        setNotificacao({ open: true, mensagem: "Tela inicial definida!" });
        handleClose();
    };

    const onSair = async () => {
        await tokenService.deleteToken();
        navigate("/login");
    };

    return (
        <>
            <IconButton onClick={handleOpen} size="small" sx={{ ml: 1 }}>
                <Avatar 
                    sx={{ width: 32, height: 32, border: `1px solid ${theme.palette.divider}` }}
                    src={usuario.foto ? `data:image/png;base64,${usuario.foto}` : undefined}
                >
                    {usuario.nome?.[0] || 'U'}
                </Avatar>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{ sx: { width: 280, mt: 1, borderRadius: 2 } }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{usuario.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{usuario.login}</Typography>
                </Box>
                <Divider />
                
                <MenuItem onClick={definirHome} disabled={!taskSelecionada}>
                    <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                    <Typography variant="body2">Definir como Tela Inicial</Typography>
                </MenuItem>

                <MenuItem onClick={() => { handleClose(); setIsTrocarSenhaModalOpen(true); }}>
                    <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                    <Typography variant="body2">Trocar Senha</Typography>
                </MenuItem>

                <Divider />
                
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>CORES DO SISTEMA</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mt: 1 }}>
                        {(Object.keys(esquemasCores) as NomeTema[]).map((nome) => (
                            <Box
                                key={nome}
                                onClick={() => alterarTema(nome)}
                                sx={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    bgcolor: esquemasCores[nome].primary,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: configTema === nome ? '2px solid black' : '1px solid transparent'
                                }}
                            >
                                {configTema === nome && <CheckCircleIcon sx={{ color: 'white', fontSize: 14 }} />}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Divider />
                
                <MenuItem onClick={onSair}>
                    <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                    <Typography variant="body2" color="error">Sair do Sistema</Typography>
                </MenuItem>
            </Menu>

            <Snackbar
                open={notificacao.open}
                autoHideDuration={3000}
                onClose={() => setNotificacao({ ...notificacao, open: false })}
                message={notificacao.mensagem}
            />

            <ModalTrocarSenha
                open={isTrocarSenhaModalOpen}
                onClose={() => setIsTrocarSenhaModalOpen(false)}
                usuarioCodigo={usuario.codigo}
            />
        </>
    );
});

export default UsuarioMenu;